import { RELATIONSHIPS } from '../../components/landing/quiz/constants';
import type { ResultsProductCardItem } from '../../components/landing/results/types';
import type {
  CarouselEmptyReason,
  CarouselSession,
  Recommendation,
  RecipientSnapshot,
  RecommendationInput,
  RecommendationProduct,
  TheaWebRelationshipEnum,
} from '../schemas';
import { decodeHtmlEntities, decodeHtmlEntitiesOpt } from './decodeHtmlEntities';
import { resolveDynamicCarouselTitle } from './dynamicCarouselTitle';
import { resolveClickoutUrl } from './sovrnClickout';

// Wire-enum → display label, mirroring `quizAnswersToRequest`. Used to find
// the matching emoji entry in `RELATIONSHIPS` when the recipient snapshot
// doesn't carry one (the quiz doesn't set `recipientSnapshot.emoji` today),
// and exported for callers that need to seed search-pill state from a
// stored recipient snapshot (the board surface seeds BoardSearchPill from
// recipientSnapshot.relationship).
export const DISPLAY_BY_RELATIONSHIP: Partial<Record<TheaWebRelationshipEnum, string>> = {
  MOM: 'Mom',
  DAD: 'Dad',
  PARTNER: 'Partner',
  SISTER: 'Sister',
  BROTHER: 'Brother',
  DAUGHTER: 'Daughter',
  SON: 'Son',
  GRANDMA: 'Grandma',
  GRANDPA: 'Grandpa',
  GRANDDAUGHTER: 'Granddaughter',
  GRANDSON: 'Grandson',
  FRIEND: 'Friend',
  OTHER: 'Other',
};

const FALLBACK_EMOJI = '✨';

function emojiForRelationship(rel: TheaWebRelationshipEnum, isMe: boolean): string {
  if (isMe) {
    return RELATIONSHIPS.find((r) => r.value === 'Me!')?.emoji ?? FALLBACK_EMOJI;
  }
  const display = DISPLAY_BY_RELATIONSHIP[rel];
  return RELATIONSHIPS.find((r) => r.value === display)?.emoji ?? FALLBACK_EMOJI;
}

const titleCase = (s: string) =>
  s.length === 0 ? s : s[0].toUpperCase() + s.slice(1).toLowerCase();

function formatInterestsLabel(interests: readonly string[]): string {
  if (interests.length === 0) return 'No interests yet';
  if (interests.length <= 2) return interests.map(titleCase).join(', ');
  const head = interests.slice(0, 2).map(titleCase).join(', ');
  return `${head} +${interests.length - 2}`;
}

export interface RecipientHeaderProps {
  personEmoji: string;
  personName: string;
  interestsLabel: string;
}

export function recipientHeaderProps(doc: Recommendation): RecipientHeaderProps {
  const { recipientSnapshot, input } = doc;
  return {
    personEmoji:
      recipientSnapshot.emoji ??
      emojiForRelationship(recipientSnapshot.relationship, recipientSnapshot.isMe),
    personName: recipientSnapshot.name,
    interestsLabel: formatInterestsLabel(input.interests),
  };
}

/**
 * Layer in-flight drawer edits on top of the snapshot-derived header props
 * so the search pill (and saved/purchased grid titles) reflect the user's
 * live draft name/emoji — both while the drawer is open AND after a
 * recipient-only auto-save on close (bug #51 followup). The rec doc's
 * snapshot stays frozen until the next regenerate, so without this override
 * the pill would show the stale pre-edit name.
 *
 * `interestsLabel` deliberately stays snapshot-driven since interest edits
 * are an algo trigger and don't take effect until "Update picks" creates
 * a new rec.
 */
export function mergeHeaderWithDraft(
  base: RecipientHeaderProps,
  draft: { name?: string; emoji?: string },
): RecipientHeaderProps {
  return {
    ...base,
    personName: draft.name && draft.name.length > 0 ? draft.name : base.personName,
    personEmoji: draft.emoji && draft.emoji.length > 0 ? draft.emoji : base.personEmoji,
  };
}

/**
 * True when a carousel session has reached a final state (pipeline finished
 * or failed). Used by the results page's refresh-snapshot pattern (bug #43):
 * during a refresh the page keeps rendering the OLD sections and only swaps
 * to the new session at a final state.
 *
 * Critical: we deliberately do NOT count "session has carouselOrder entries"
 * as ready. The agent writes carouselOrder BEFORE populating each carousel's
 * products, and a partially-streaming session would show as half-loaded
 * carousels — exactly the flicker bug #43 set out to fix. Wait for the full
 * pipeline (`COMPLETED`) or a hard failure (`FAILED`); the page-level 30s
 * timeout covers any agent that hangs.
 *
 * `null`/`undefined` count as not-ready (still loading).
 */
export function isSessionReadyToDisplay(
  session: CarouselSession | null | undefined,
): boolean {
  if (!session) return false;
  return session.status === 'COMPLETED' || session.status === 'FAILED';
}

export function productToCardItem(p: RecommendationProduct): ResultsProductCardItem {
  // Prefer mobile CDN webp, then desktop CDN, then raw scrape URL. ResultsProductCard
  // doesn't yet take separate desktop/mobile srcsets, so we pick the first usable.
  // The same preference order, kept as a full list, feeds the product-detail
  // quick-view gallery (`galleryUrls`).
  const gallery =
    (p.images_cdn_mobile?.length
      ? p.images_cdn_mobile
      : p.images_cdn?.length
        ? p.images_cdn
        : p.images) ?? [];
  const imageUrl = gallery[0] ?? '';
  // Sovrn affiliate linking is allowlist-gated per merchant: the wrapped
  // affiliateUrl is used only for merchants measured at 100% deep-link
  // survival; everything else clicks through to the plain merchant URL.
  // Measurement history + confirmed-broken list live in sovrnClickout.ts.
  const productUrl = resolveClickoutUrl(p);
  return {
    id: p.id,
    imageUrl,
    // Frozen sessions can carry entity-encoded merchant text ("&#8217;") —
    // decode at the display seam; catalog repairs can't reach old sessions.
    title: decodeHtmlEntities(p.title),
    brand: decodeHtmlEntitiesOpt(p.brand),
    price: p.price,
    productUrl,
    galleryUrls: gallery,
    // Prefer the BE-generated gift blurb (display_description); fall back to the
    // raw scraped description, which ProductDetailSheet then cleans for display.
    // Blurbs are already clean, so that cleanup is a near-no-op on them.
    description: p.display_description?.trim() || p.description,
    // Raw merchant URL drives the retailer display name only — the affiliate
    // wrap (productUrl) is the wrong host to read ("redirect.sovrn.com").
    retailerUrl: p.url,
  };
}

export interface ResultsCarouselSection {
  id: string;
  title: string;
  products: ResultsProductCardItem[];
  /** True while this carousel is still being generated (no products yet) —
   *  drives the loading skeleton. Sourced from the live carousel status. */
  loading?: boolean;
  /** Why the BACKEND finished this rail with zero products (THE-559). Present
   *  only on a rail that arrived empty; it is what lets the board tell a chip
   *  that genuinely came back with nothing apart from a rail the price slider
   *  emptied. See `CarouselEmptyReason`. */
  emptyReason?: CarouselEmptyReason;
}

// Convert the `CarouselSession` document into the carousel-section view
// model the results page renders. The `recipientSnapshot` is used by the
// dynamic title resolver to apply chip→title overrides, "Perfect for X"
// special chips, and pronoun genderization (bug #17). When no snapshot is
// passed (older call sites + tests), titles fall through to the BE's
// `displayName` unchanged.
//
// The `recipientInput` arg is no longer read by the title resolver but is
// kept on the signature so existing call sites compile without churn.
export function carouselsToSections(
  session: CarouselSession,
  _recipientInput?: RecommendationInput,
  recipientSnapshot?: RecipientSnapshot,
): ResultsCarouselSection[] {
  const relationshipDisplay = recipientSnapshot
    ? recipientSnapshot.isMe
      ? 'Me'
      : DISPLAY_BY_RELATIONSHIP[recipientSnapshot.relationship]
    : undefined;

  return session.carouselOrder
    .filter((key) => key in session.carousels)
    .map((key) => {
      const c = session.carousels[key];
      const title = recipientSnapshot
        ? resolveDynamicCarouselTitle({
            chip: key,
            fallbackDisplayName: c.displayName,
            gender: recipientSnapshot.gender,
            relationshipDisplay,
          })
        : c.displayName;
      return {
        id: key,
        title,
        products: c.products.map(productToCardItem),
        loading: c.loading ?? false,
        // Carried through unchanged so the page can keep an explicitly-picked
        // chip rail that came back empty (THE-559) instead of filtering it out.
        ...(c.emptyReason ? { emptyReason: c.emptyReason } : {}),
      };
    });
}
